import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { buffer, filter, map, tap } from 'rxjs';

import { SidebarCustomComponents } from '@features/sidebar';
import { CloseSidebarCustomSuccess, OpenSidebarCustomSuccess } from '@features/sidebar/infrastructure/state/sidebar.actions';
import { ProductFilterItem } from '@pages/category';
import { ProductFilterService } from '@pages/category/services/product-filter.service';
import { getFiltersFilters, getOrderPriceProductsId } from '@pages/category/store/category-page.selectors';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { GA4ImActionType, Ga4ImType, Ga4ImTypeForFilter } from '@shared/analytics/GA4/models/event-data.model';
import { GA4EventType } from '@shared/analytics/GA4/models/general.model';
import { Ga4Event } from '@shared/analytics/GA4/store/ga4-analytics.actions';

// #region Utilities

export const getFiltersApplied = (store: Store): ProductFilterItem[] => {
    const filters = store.selectSignal(getFiltersFilters)();
    const orderState = store.selectSignal(getOrderPriceProductsId)();

    const appliedFilters = ProductFilterService.getFilterApplied(filters);
    if (orderState?.isApplied) {
        const order = { filterGroup: 'order', value: orderState.orderType } as ProductFilterItem;
        return [...appliedFilters, order];
    }
    return appliedFilters;
};

export const mapFilterGroupToImType = (filterGroup: string): Ga4ImType => {
    return {
        catFilter: Ga4ImTypeForFilter.characteristics,
        colFilter: Ga4ImTypeForFilter.colors,
        discountFilter: Ga4ImTypeForFilter.price,
        maxPriceFilter: Ga4ImTypeForFilter.price,
        minPriceFilter: Ga4ImTypeForFilter.price,
        order: Ga4ImTypeForFilter.order_by,
        sizeFilter: Ga4ImTypeForFilter.size,
        typeFilter: Ga4ImTypeForFilter.type,
        XCALFILTER: Ga4ImTypeForFilter.composition,
    }?.[filterGroup];
};

export const getNewAppliedFiltersByGroup = (
    previousFilters: ProductFilterItem[],
    currentFilters: ProductFilterItem[]
): Record<string, string[]> => {
    const newFilters = currentFilters.filter((current) =>
        !previousFilters.find((previous) =>
            (previous?.value || previous?.name || previous?.price) === (current?.value || current?.name || current?.price)));

    return newFilters.reduce((acc, _filter) => {
        const filterValue = _filter?.value || _filter?.name || _filter?.price;
        if (typeof acc[_filter.filterGroup] === 'undefined') {
            acc[_filter.filterGroup] = [filterValue];
        } else {
            acc[_filter.filterGroup].push(filterValue);
        }
        return acc;
    }, {});
};

// #endregion

// #region Effects

export const EventInteractionFilterOpen$ = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(OpenSidebarCustomSuccess),
        filter(({ componentName }) => componentName === SidebarCustomComponents.FILTER),
        map(() => Ga4Event({
            name: GA4EventType.interaction_filter,
            data: {
                imActionType: GA4ImActionType.abrir_filtro,
            },
        }))
    );
}, { functional: true });

export const EventInteractionFilterApply$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(OpenSidebarCustomSuccess),
        filter(({ componentName }) => componentName === SidebarCustomComponents.FILTER),
        map(() => getFiltersApplied(store)),
        // Proceed with received data after filters sidebar is closed
        buffer(actions$.pipe(
            ofType(CloseSidebarCustomSuccess),
            filter(({ componentName }) => componentName === SidebarCustomComponents.FILTER)
        )),
        tap(([initialfilters]) => {
            const filtersAfterClosed = getFiltersApplied(store);
            const filtersByGroup = getNewAppliedFiltersByGroup(initialfilters, filtersAfterClosed);

            // biome-ignore lint/complexity/noForEach: Used for a small array, without chained methods
            Object.entries(filtersByGroup).forEach(([type, value]: [string, string[]]) => {
                store.dispatch(Ga4Event({
                    name: GA4EventType.interaction_filter,
                    eventFrom: EventFrom.filter,
                    data: {
                        imActionType: GA4ImActionType.aplicar_filtro,
                        imType: mapFilterGroupToImType(type),
                        imDetail: value.join(','),
                    },
                }));
            });
        }),
    );
}, { functional: true, dispatch: false });

export const GA4AnalyticsCategoryEffects = {
    EventInteractionFilterOpen$,
    EventInteractionFilterApply$,
};

// #endregion
